import {
  Component,
  OnInit,
  AfterViewInit,
  signal,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime } from 'rxjs';

import { InboundService } from '../inbound.service';
import { WarehouseWarrantSaveDTO } from '@app/api/model/warehouseWarrantSaveDTO';
import { InboundType, InboundWarrantStatus } from '../inbound.config';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
  WarehouseQueryResVO,
  OutboundDeliveryOrderService,
  OutboundDeliveryOrderPageDTO,
} from '@app/api/stock-service';
import { PurchaseOrderService } from '@app/feature/center-business/procurement/purchase-order/services/purchase-order.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BASE_PATH as STOCK_BASE_PATH } from '@app/api/stock-service/variables';
import { from, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { withPaginatedFormFields } from '@shared/utils/paginated-field.helpers';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import {
  AddFormComponent,
  FormField,
  TableSection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import {
  INBOUND_ADD_FORM_CONFIG,
  INBOUND_LOGISTICS_TABLE_SECTION,
  INBOUND_DETAIL_TABLE_SECTION,
  INBOUND_HEADER_ACTIONS,
  LogisticsItem,
  DetailItem,
} from './inbound-add.config.js';
import { LogisticsDialogComponent } from './components/logistics-dialog/logistics-dialog.component';
import { DetailDialogComponent } from './components/detail-dialog/detail-dialog.component';
import { ConfirmationDialogComponent } from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-inbound-add',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    LogisticsDialogComponent,
    DetailDialogComponent,
    ConfirmationDialogComponent,
    ButtonModule,
    ToastModule,
    FormsModule,
  ],
  providers: [MessageService],
  templateUrl: './inbound-add.component.html',
  styleUrl: './inbound-add.component.css',
})
export class InboundAddComponent
  implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate
{
  @ViewChild('addForm') addFormComponent: any;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly inboundService = inject(InboundService);
  private readonly translateService = inject(TranslateService);
  private readonly warehouseService = inject(WarehouseControllerService);
  private readonly purchaseOrderService = inject(PurchaseOrderService);
  private readonly outboundDeliveryOrderService = inject(
    OutboundDeliveryOrderService,
  );
  private readonly http = inject(HttpClient);
  private readonly basePath = inject(STOCK_BASE_PATH);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly destroy$ = new Subject<void>();
  private readonly cellValueChange$ = new Subject<{
    rowData: any;
    prop: string;
    value: any;
  }>();

  // Flag to prevent warehouse mapping when clearing notice
  private isClearingNotice = false;

  // Store translated section titles for consistent comparison
  private itemDetailsSectionTitle = '';
  private logisticsSectionTitle = '';

  // Form configuration
  formFields = signal<FormField[]>(INBOUND_ADD_FORM_CONFIG);
  tableSections = signal<TableSection[]>([
    INBOUND_DETAIL_TABLE_SECTION,
    INBOUND_LOGISTICS_TABLE_SECTION,
  ]);
  headerActions = signal<ActionButton[]>(INBOUND_HEADER_ACTIONS);

  // Data
  logisticsList = signal<LogisticsItem[]>([]);
  detailList = signal<DetailItem[]>([]);
  selectedLogistics = signal<LogisticsItem[]>([]);
  selectedDetails = signal<DetailItem[]>([]);
  loading = signal(false);
  currentFormData: any = null;
  private skipCanDeactivate = false;

  // Dialog states
  showLogisticsDialog = signal(false);
  showDetailDialog = signal(false);
  showDeleteConfirmDialog = signal(false);
  editingLogistics = signal<LogisticsItem | null>(null);

  // Confirmation dialog state
  deleteConfirmData = signal<{
    type: 'single' | 'multiple';
    item?: DetailItem;
    items?: DetailItem[];
    count?: number;
  } | null>(null);

  // Warehouse dropdown options with id, label (code - name), code, and name
  warehouseOptions = signal<
    {
      id: number;
      label: string;
      code: string;
      name: string;
      value: string; // For dropdown compatibility, value = code
    }[]
  >([]);

  // Purchase Order dropdown options
  purchaseOrderOptions = signal<
    {
      label: string;
      value: string; // PO series/code
    }[]
  >([]);
  // Outbound Delivery Order dropdown options (for transfer order)
  outboundDeliveryOrderOptions = signal<
    {
      label: string;
      value: string; // Outbound delivery order code
      id?: number; // ODO ID for API calls
    }[]
  >([]);

  // Inbound Notice dropdown options
  inboundNoticeOptions = signal<
    {
      label: string;
      value: string; // Notice ID
      code: string; // Notice code for display
    }[]
  >([]);

  ngOnInit(): void {
    this.initializeConfigurations();
    this.loadOptions();
    this.updateFormFields();
    this.updateLogisticsTableData();
    this.updateDetailTableData();
    this.updateHeaderActions();

    // Check if creating from notice (via query params)
    const noticeId = this.route.snapshot.queryParams['noticeId'];
    if (noticeId) {
      // Load warehouse options first to map warehouseCode to warehouseName
      // Then load notice data and fill form after warehouses are loaded
      this.loadNoticeDataAndFillForm(noticeId);
    } else {
      // Normal case: load options based on default inputType
      const defaultInputType =
        this.currentFormData?.inputType || InboundType.PURCHASE_ORDER;
      this.loadFrontRecordCodeOptions(defaultInputType);
      // Show warning if detail list is empty (only when not loading from notice)
      this.showWarningIfDetailListEmpty();
    }

    // Set up debounced cell value changes to prevent input focus loss
    this.cellValueChange$
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe((event) => {
        this.updateDetailListFromCellChange(event);
      });
  }

  ngAfterViewInit(): void {
    // ViewChild is available after view initialization
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cellValueChange$.complete();
  }

  onFormAction(action: string): void {
    switch (action) {
      case 'save':
        if (this.loading()) {
          return;
        }
        this.loading.set(true); // Set loading state immediately
        this.onSave();
        break;
      case 'cancel':
        this.onBack();
        break;
    }
  }

  onTableAction(event: { action: string; data?: any; section?: string }): void {
    switch (event.action) {
      case 'addLogistics':
        this.onAddLogistics();
        break;
      case 'addDetail':
        this.onAddDetail();
        break;
      case 'editLogistics':
        this.onEditLogistics(event.data);
        break;
      case 'deleteLogistics':
        this.onDeleteLogistics(event.data);
        break;
      case 'deleteDetail':
        this.showDeleteConfirmation('single', event.data);
        break;
      case 'deleteSelectedLogistics':
        this.onDeleteSelectedLogistics();
        break;
      case 'deleteSelectedDetail':
        this.showDeleteConfirmation('multiple');
        break;
      case 'selectionChange':
        this.onSelectionChange(event.section, event.data);
        break;
      case 'cellValueChange':
        this.onCellValueChange(event.data);
        break;
    }
  }

  onFormValueChanges(formData: any): void {
    // Check if input type changed before updating currentFormData
    const inputTypeChanged =
      formData.inputType &&
      formData.inputType !== this.currentFormData?.inputType;

    // Check if notice is being cleared - if so, clear warehouse data in formData first
    const currentNoticeId = formData.inboundNoticeId;
    const previousNoticeId = this.currentFormData?.inboundNoticeId;
    if (!currentNoticeId && previousNoticeId) {
      // Notice is being cleared - clear warehouse data in formData before processing
      formData = {
        ...formData,
        receivingLogicWarehouseCode: '',
        receivingLogicWarehouseName: '',
        receivingLogicWarehouseId: null,
      };
    }

    // Handle warehouse dropdown changes and get updated form data
    let updatedFormData = this.handleWarehouseDropdownChange(formData);

    // Handle PO/ODO selection change based on inputType
    const currentInputType =
      formData.inputType ||
      this.currentFormData?.inputType ||
      InboundType.PURCHASE_ORDER;
    if (currentInputType === InboundType.PURCHASE_ORDER) {
      updatedFormData = this.handlePurchaseOrderChange(
        updatedFormData || formData,
      );
    } else if (currentInputType === InboundType.TRANSFER_ORDER) {
      updatedFormData = this.handleOutboundDeliveryOrderChange(
        updatedFormData || formData,
      );
    }

    // Handle notice selection change
    updatedFormData = this.handleInboundNoticeChange(
      updatedFormData || formData,
    );

    // Store the current form data (use updated data if handlers modified it)
    this.currentFormData = updatedFormData || formData;

    // Update form fields based on input type changes
    if (inputTypeChanged) {
      this.updateFormFields();
      // Load options for frontRecordCode based on inputType
      this.loadFrontRecordCodeOptions(formData.inputType);
    }
  }

  private handleWarehouseDropdownChange(formData: any): any {
    const form = this.getForm();
    if (!form) return formData;

    // Don't auto-map if notice is being cleared (warehouse should be cleared by notice handler)
    if (this.isClearingNotice) {
      return formData;
    }

    const currentNoticeId = formData.inboundNoticeId;
    const previousNoticeId = this.currentFormData?.inboundNoticeId;
    if (!currentNoticeId && previousNoticeId) {
      // Notice is being cleared, let handleInboundNoticeChange handle the clearing
      return formData;
    }

    // Auto-map warehouse name from warehouse code
    const currentWarehouseCode =
      formData.receivingLogicWarehouseCode ||
      formData.receivingLogicWarehouseName;
    const previousWarehouseCode =
      this.currentFormData?.receivingLogicWarehouseCode ||
      this.currentFormData?.receivingLogicWarehouseName;

    // Check if warehouse code changed
    if (
      currentWarehouseCode &&
      currentWarehouseCode !== previousWarehouseCode
    ) {
      const foundWarehouse = this.findWarehouseByCode(currentWarehouseCode);

      if (foundWarehouse) {
        // Auto-populate warehouse name from code
        const nameControl = form.get('receivingLogicWarehouseName');
        if (nameControl) {
          nameControl.setValue(foundWarehouse.name, { emitEvent: false });
        }

        // Update hidden ID control
        const idControl = form.get('receivingLogicWarehouseId');
        if (idControl) {
          idControl.setValue(foundWarehouse.id, { emitEvent: false });
        }

        // Return updated form data with mapped name
        return {
          ...formData,
          receivingLogicWarehouseCode: foundWarehouse.code,
          receivingLogicWarehouseName: foundWarehouse.name, // Store actual name for API
          receivingLogicWarehouseId: foundWarehouse.id,
        };
      } else {
        // Warehouse code not found in options, clear name
        const nameControl = form.get('receivingLogicWarehouseName');
        if (nameControl) {
          nameControl.setValue('', { emitEvent: false });
        }
      }
    } else if (!currentWarehouseCode && previousWarehouseCode) {
      // Warehouse code was cleared, also clear warehouse name
      const nameControl = form.get('receivingLogicWarehouseName');
      if (nameControl) {
        nameControl.setValue('', { emitEvent: false });
      }
      const idControl = form.get('receivingLogicWarehouseId');
      if (idControl) {
        idControl.setValue(null, { emitEvent: false });
      }
    }

    // Return original formData if no changes were made
    return formData;
  }

  private findWarehouseByCode(
    code: string,
  ): { id: number; code: string; name: string } | null {
    const cached = this.warehouseOptionsService.getWarehouseByCode(code);
    if (cached?.code) {
      return {
        id: Number(cached.id) || 0,
        code: cached.code,
        name: cached.name || '',
      };
    }
    const warehouses = this.warehouseDataMap();
    return warehouses.get(code);
  }

  warehouseDataMap = signal<Map<string, any>>(new Map());

  onSelectionChange(section: string | undefined, selectedItems: any[]): void {
    // Find the section by name or title for comparison
    const targetSection = this.tableSections().find(
      (s) => s.name === section || s.title === section,
    );

    if (targetSection?.name === 'logistics_information') {
      this.selectedLogistics.set(selectedItems);
      this.updateLogisticsTableData();
    } else if (targetSection?.name === 'item_details') {
      this.selectedDetails.set(selectedItems);
      this.updateDetailTableData();
    }
  }

  private onSave(): void {
    // Wait for any pending debounced cell value changes to complete
    setTimeout(() => {
      this.buildAndSubmitPayload();
    }, 150);
  }

  private buildAndSubmitPayload(): void {
    // Validate warehouse fields are required (only for normal create, not when creating from notice)
    const formData = this.currentFormData || {};
    const noticeId = this.route.snapshot.queryParams['noticeId'];
    if (!noticeId) {
      // Only validate when NOT creating from notice
      if (
        !formData.receivingLogicWarehouseName ||
        !formData.receivingLogicWarehouseCode
      ) {
        this.loading.set(false); // Reset loading state on validation error
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'inbound.messages.validation_error',
          ),
          detail: this.translateService.instant(
            'inbound.messages.warehouse_fields_required',
          ),
        });
        return;
      }
    }

    // Validate that at least 1 item detail is required
    if (this.detailList().length === 0) {
      this.loading.set(false); // Reset loading state on validation error
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'inbound.messages.validation_error',
        ),
        detail: this.translateService.instant(
          'inbound.messages.at_least_one_item_detail_required',
        ),
      });
      return;
    }

    // Get form data - use getRawValue() to get values from disabled controls
    const form = this.getForm();
    let rawFormData: any = {};
    if (form) {
      rawFormData = form.getRawValue(); // This includes disabled controls
    }

    // Merge form data: currentFormData + rawFormData (prioritize rawFormData for disabled controls)
    const mergedFormData = {
      ...formData,
      ...rawFormData,
    };

    const inputType = mergedFormData.inputType || InboundType.PURCHASE_ORDER;

    // Get inboundNoticeCode from the form
    // Case 1: Creating from notice (via URL) - noticeId is already set as notice code (string)
    // Case 2: Normal mode - user selected from dropdown - value is notice ID, need to map to code
    let inboundNoticeCode = '';
    const inboundNoticeControl = form?.get('inboundNoticeId');
    if (inboundNoticeControl && inboundNoticeControl.value) {
      const noticeValue = inboundNoticeControl.value;

      if (noticeId) {
        // Case 1: Creating from notice via URL - value is already the notice code (string)
        inboundNoticeCode = String(noticeValue).trim();
      } else {
        // Case 2: Normal mode - value is notice ID, need to find code from options
        const noticeIdStr = String(noticeValue).trim();
        const options = this.inboundNoticeOptions();
        const selectedOption = options.find(
          (opt: any) =>
            opt.value === noticeIdStr || opt.value?.toString() === noticeIdStr,
        );

        if (selectedOption?.code) {
          inboundNoticeCode = selectedOption.code;
        } else if (
          typeof noticeValue === 'string' &&
          noticeValue.length > 0 &&
          !/^\d+$/.test(noticeValue)
        ) {
          // If it's already a code (string that doesn't look like just digits), use it directly
          inboundNoticeCode = noticeValue;
        }
      }
    }

    // Validate frontRecordCode (chứng từ gốc) - required field
    const frontRecordCode = mergedFormData.frontRecordCode || '';
    if (!frontRecordCode || frontRecordCode.trim() === '') {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail:
          this.translateService.instant(
            'inbound.messages.front_record_code_required',
          ) || 'Chứng từ gốc không được để trống',
      });
      return;
    }

    // Validate warehouse - must have both code and ID
    const warehouseCode = mergedFormData.receivingLogicWarehouseCode;
    const warehouseId = mergedFormData.receivingLogicWarehouseId;
    const warehouseName = mergedFormData.receivingLogicWarehouseName;

    if (!warehouseCode || !warehouseId) {
      this.loading.set(false);
      const errorMsg = !warehouseCode
        ? this.translateService.instant(
            'inbound.messages.warehouse_code_required',
          )
        : !warehouseId
          ? this.translateService.instant(
              'inbound.messages.warehouse_not_found',
              { code: warehouseCode },
            )
          : this.translateService.instant(
              'inbound.messages.invalid_warehouse_info',
            );
      this.errorDialogService.showError(errorMsg);
      return;
    }

    const foundWarehouse = warehouseCode
      ? this.findWarehouseByCode(warehouseCode)
      : null;
    if (warehouseCode && !foundWarehouse) {
      this.loading.set(false);
      this.errorDialogService.showError(
        this.translateService.instant(
          'inbound.messages.warehouse_not_found_with_code',
          { code: warehouseCode },
        ),
      );
      return;
    }
    if (foundWarehouse?.id && foundWarehouse.id !== warehouseId) {
      mergedFormData.receivingLogicWarehouseId = foundWarehouse.id;
    }

    // Build payload based on inbound type
    const payload: WarehouseWarrantSaveDTO & {
      status?: string;
      inboundNoticeCode?: string;
    } = {
      // Always included fields
      tenantId: mergedFormData.tenantId,
      inputType: inputType,
      status: mergedFormData.status || InboundWarrantStatus.CREATED, // Add missing status field
      receivingLogicWarehouseId: mergedFormData.receivingLogicWarehouseId,
      receivingLogicWarehouseName: mergedFormData.receivingLogicWarehouseName,
      receivingLogicWarehouseCode: mergedFormData.receivingLogicWarehouseCode,
      frontRecordCode: frontRecordCode.trim(),
      frontRecordId: mergedFormData.frontRecordId,
      platformOrderCode: mergedFormData.platformOrderCode,
      orderCode: mergedFormData.orderCode,
      // Add inbound notice code if available
      ...(inboundNoticeCode && { inboundNoticeCode: inboundNoticeCode }),
      logisticsList: this.logisticsList().map((item) => ({
        logisticsCompanyId: item.logisticsCompanyId,
        logisticsCompanyName: item.logisticsCompanyName,
        logisticsNo: item.logisticsNo,
      })),
      detailList: this.detailList().map((item) => ({
        skuCode: item.skuCode,
        skuName: item.skuName,
        spuCode: item.spuCode,
        spuName: item.spuName,
        plannedQty: item.plannedQty,
      })),
    };

    // Add fields based on inbound type
    if (inputType === InboundType.RETURN_ORDER) {
      // Only for Return Order: All returns* fields + optional address hierarchy
      payload.returnsName = mergedFormData.returnsName;
      payload.returnsAddress = mergedFormData.returnsAddress;
      payload.returnsTel = mergedFormData.returnsTel;
      payload.returnsMobile = mergedFormData.returnsMobile;
      payload.country = mergedFormData.country;
      payload.province = mergedFormData.province;
      payload.city = mergedFormData.city;
      payload.district = mergedFormData.district;
    } else if (inputType === InboundType.PURCHASE_ORDER) {
      // Only for Purchase Order: sorBuyerNikeName, orderMsg, buyMsg
      payload.sorBuyerNikeName = mergedFormData.sorBuyerNikeName;
      payload.orderMsg = mergedFormData.orderMsg;
      payload.buyMsg = mergedFormData.buyMsg;
    } else if (inputType === InboundType.TRANSFER_ORDER) {
      // Only for Transfer Order: storeId, storeName, channelTypeCode
      payload.storeId = mergedFormData.storeId;
      payload.storeName = mergedFormData.storeName;
      payload.channelTypeCode = mergedFormData.channelTypeCode;
    }

    this.loading.set(true);
    this.inboundService
      .createInboundNotice(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'inbound.messages.inbound_warrant_created_successfully',
            ),
          });
          // Add delay to allow toast message to display before navigation
          setTimeout(() => {
            const tab =
              this.route.snapshot.queryParams['tab'] || 'inbound-warrant';
            this.router.navigate(['/stock/inbound'], {
              queryParams: { tab },
            });
          }, 500);
        },
        error: (error) => {
          this.loading.set(false);

          // Extract error message from API and translate it
          const errorMessage =
            error?.error?.message || extractErrorMessage(error);
          const translatedMessage =
            this.errorTranslatorService.translate(errorMessage);

          // Show error in dialog
          this.errorDialogService.showError(translatedMessage);
        },
      });
  }

  private onCancel(): void {
    this.router.navigate(['/stock/inbound']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private onAddLogistics(): void {
    this.editingLogistics.set(null);
    this.showLogisticsDialog.set(true);
  }

  private onAddDetail(): void {
    this.showDetailDialog.set(true);
  }

  private onEditLogistics(data: any): void {
    this.editingLogistics.set(data);
    this.showLogisticsDialog.set(true);
  }

  private onDeleteLogistics(data: any): void {
    const updatedList = this.logisticsList().filter(
      (item) => item.id !== data.id,
    );
    this.logisticsList.set(updatedList);
    this.updateLogisticsTableData();
  }

  /**
   * Show confirmation dialog for deleting item details
   */
  private showDeleteConfirmation(
    type: 'single' | 'multiple',
    item?: DetailItem,
  ): void {
    if (type === 'single' && item) {
      this.deleteConfirmData.set({
        type: 'single',
        item: item,
        count: 1,
      });
    } else if (type === 'multiple') {
      const selectedItems = this.selectedDetails();
      if (selectedItems.length === 0) return;

      this.deleteConfirmData.set({
        type: 'multiple',
        items: selectedItems,
        count: selectedItems.length,
      });
    }

    this.showDeleteConfirmDialog.set(true);
  }

  /**
   * Handle confirmation dialog button clicks
   */
  onDeleteConfirmAction(action: string): void {
    if (action === 'confirm') {
      const confirmData = this.deleteConfirmData();
      if (confirmData) {
        if (confirmData.type === 'single' && confirmData.item) {
          this.onDeleteDetail(confirmData.item);
        } else if (confirmData.type === 'multiple' && confirmData.items) {
          this.onDeleteSelectedDetail();
        }
      }
    }

    // Close dialog and reset state
    this.showDeleteConfirmDialog.set(false);
    this.deleteConfirmData.set(null);
  }

  /**
   * Get confirmation dialog title
   */
  getDeleteConfirmTitle(): string {
    const confirmData = this.deleteConfirmData();
    if (confirmData?.type === 'single') {
      return 'inbound.confirm.delete_item_title';
    } else {
      return 'inbound.confirm.delete_items_title';
    }
  }

  /**
   * Get confirmation dialog message
   */
  getDeleteConfirmMessage(): string {
    const confirmData = this.deleteConfirmData();
    if (confirmData?.type === 'single' && confirmData.item) {
      // For single item, we need to handle the interpolation manually
      // since the translate pipe doesn't support interpolation in the template
      return this.translateService.instant(
        'inbound.confirm.delete_item_message',
        {
          skuCode: confirmData.item.skuCode,
          skuName: confirmData.item.skuName,
        },
      );
    } else if (confirmData?.type === 'multiple' && confirmData.count) {
      return this.translateService.instant(
        'inbound.confirm.delete_items_message',
        {
          count: confirmData.count,
        },
      );
    }
    return '';
  }

  /**
   * Get confirmation dialog buttons
   */
  getDeleteConfirmButtons(): any[] {
    return [
      {
        label: 'common.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label: 'common.actions.delete',
        action: 'confirm',
        severity: 'danger',
      },
    ];
  }

  private onDeleteDetail(data: any): void {
    const updatedList = this.detailList().filter((item) => item.id !== data.id);
    this.detailList.set(updatedList);
    this.updateDetailTableData();

    // Show success message
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant(
        'inbound.messages.item_detail_deleted_successfully',
      ),
    });

    // Show warning if list becomes empty
    this.showWarningIfDetailListEmpty();
  }

  private onDeleteSelectedLogistics(): void {
    const selectedItems = this.selectedLogistics();
    if (selectedItems.length === 0) return;

    const selectedIds = selectedItems.map((item) => item.id);
    const updatedList = this.logisticsList().filter(
      (item) => !selectedIds.includes(item.id),
    );
    this.logisticsList.set(updatedList);
    this.selectedLogistics.set([]);
    this.updateLogisticsTableData();

    // Show success message
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant(
        'inbound.messages.logistics_items_deleted_successfully',
        { count: selectedItems.length },
      ),
    });
  }

  private onDeleteSelectedDetail(): void {
    const selectedItems = this.selectedDetails();
    if (selectedItems.length === 0) return;

    const selectedIds = selectedItems.map((item) => item.id);
    const updatedList = this.detailList().filter(
      (item) => !selectedIds.includes(item.id),
    );
    this.detailList.set(updatedList);
    this.selectedDetails.set([]);
    this.updateDetailTableData();

    // Show success message
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant(
        'inbound.messages.item_details_deleted_successfully',
        { count: selectedItems.length },
      ),
    });

    // Show warning if list becomes empty
    this.showWarningIfDetailListEmpty();
  }

  private updateFormFields(): void {
    const currentInputType =
      this.currentFormData?.inputType || InboundType.PURCHASE_ORDER;

    // Filter form fields based on input type
    const filteredFields = INBOUND_ADD_FORM_CONFIG.filter((field) => {
      // Always show basic information and preceding document information
      if (
        field.section === 'inbound.sections.basic_information' ||
        field.section === 'inbound.sections.preceding_document_information'
      ) {
        return true;
      }

      // Show sections based on input type
      if (currentInputType === InboundType.PURCHASE_ORDER) {
        // Hide customer_information section - only show warehouse information
        return field.section === 'inbound.sections.warehouse_information';
      } else if (currentInputType === InboundType.TRANSFER_ORDER) {
        return field.section === 'inbound.sections.warehouse_information';
      } else if (currentInputType === InboundType.RETURN_ORDER) {
        return (
          field.section === 'inbound.sections.returner_information' ||
          field.section === 'inbound.sections.warehouse_information'
        );
      }

      return false;
    });

    const currentFormFields = this.formFields();

    const translatedFields = filteredFields.map((field) => {
      const translatedField = {
        ...field,
        label: this.translateService.instant(field.label),
        placeholder: field.placeholder
          ? this.translateService.instant(field.placeholder)
          : field.placeholder,
      };

      // Preserve PO options if they exist
      if (field.controlName === 'frontRecordCode') {
        const currentField = currentFormFields.find(
          (f) => f.controlName === field.controlName,
        );
        if (currentField?.options && currentField.options.length > 0) {
          translatedField.options = [...currentField.options];
        }
      }

      // Preserve notice options if they exist
      if (field.controlName === 'inboundNoticeId') {
        const currentField = currentFormFields.find(
          (f) => f.controlName === field.controlName,
        );
        if (currentField?.options && currentField.options.length > 0) {
          translatedField.options = [...currentField.options];
        }
      }

      // Translate dropdown options if present
      if (field.type === 'dropdown' && field.options) {
        const translatedOptions = field.options.map((option) => ({
          ...option,
          label: this.translateService.instant(option.label),
        }));
        translatedField.options = translatedOptions;
      }

      return translatedField;
    });

    this.formFields.set(
      withPaginatedFormFields(translatedFields, {
        receivingLogicWarehouseName: {
          loadPage: (params) =>
            this.warehouseOptionsService
              .getOptionsPage(params, { valueKey: 'code' })
              .pipe(
                map((page) => ({
                  ...page,
                  options: page.options.map((option) => ({
                    label: option.label.includes(' - ')
                      ? option.label.split(' - ').slice(1).join(' - ')
                      : option.label,
                    value: option.value,
                  })),
                })),
              ),
        },
      }).map((field) =>
        field.controlName === 'receivingLogicWarehouseName'
          ? { ...field, type: 'searchableDropdown' as const }
          : field,
      ),
    );
  }

  private updateLogisticsTableData(): void {
    const logisticsData = this.logisticsList().map((item, index) => ({
      id: item.id || index + 1,
      logisticsCompanyName: item.logisticsCompanyName,
      logisticsNo: item.logisticsNo,
      logisticsCompanyId: item.logisticsCompanyId,
    }));

    // Update the logistics section in table sections
    const updatedSections = this.tableSections().map((section) => {
      // Use the stable name field for comparison
      if (section.name === 'logistics_information') {
        return {
          ...section,
          data: logisticsData,
          title: this.translateService.instant(
            'inbound.sections.logistics_information',
          ),
          columns: section.columns.map((col) => ({
            ...col,
            label: this.translateService.instant(col.label),
          })),
          actions:
            section.actions?.map((action) => {
              const translatedAction = {
                ...action,
                label: this.translateService.instant(action.label),
              };

              // Enable/disable delete button based on selection
              if (action.action === 'deleteSelectedLogistics') {
                translatedAction.disabled =
                  this.selectedLogistics().length === 0;
              }

              return translatedAction;
            }) || [],
        };
      }
      return section;
    });
    this.tableSections.set(updatedSections);
  }

  private updateDetailTableData(): void {
    const detailData = this.detailList().map((item, index) => ({
      id: item.id || index + 1,
      skuCode: item.skuCode,
      skuName: item.skuName,
      spuCode: item.spuCode,
      spuName: item.spuName,
      plannedQty: item.plannedQty,
      batchNumber: item.batchNumber || '',
      expiryDate: item.expiryDate || '',
    }));

    // Update the detail section in table sections
    const updatedSections = this.tableSections().map((section) => {
      // Use the stable name field for comparison
      if (section.name === 'item_details') {
        return {
          ...section,
          data: detailData,
          title: this.translateService.instant('inbound.sections.item_details'),
          columns: section.columns.map((col) => ({
            ...col,
            label: this.translateService.instant(col.label),
          })),
          actions:
            section.actions?.map((action) => {
              const translatedAction = {
                ...action,
                label: this.translateService.instant(action.label),
              };

              // Enable/disable delete button based on selection
              if (action.action === 'deleteSelectedDetail') {
                translatedAction.disabled = this.selectedDetails().length === 0;
              }

              return translatedAction;
            }) || [],
        };
      }
      return section;
    });
    this.tableSections.set(updatedSections);
  }

  onLogisticsDialogConfirm(logisticsItem: LogisticsItem): void {
    if (this.editingLogistics()) {
      // Update existing logistics item
      const updatedList = this.logisticsList().map((item) =>
        item.id === logisticsItem.id ? logisticsItem : item,
      );
      this.logisticsList.set(updatedList);
    } else {
      // Add new logistics item
      this.logisticsList.set([...this.logisticsList(), logisticsItem]);
    }
    this.updateLogisticsTableData();
  }

  onDetailDialogConfirm(detailItem: DetailItem | DetailItem[]): void {
    // Add new detail item(s)
    if (Array.isArray(detailItem)) {
      // Multiple items - add all at once
      const newList = [...this.detailList(), ...detailItem];
      this.detailList.set(newList);

      // Show success message for multiple items
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.messages.success'),
        detail: this.translateService.instant(
          'inbound.messages.item_details_added_successfully',
          { count: detailItem.length },
        ),
      });
    } else {
      // Single item - add one item
      const newList = [...this.detailList(), detailItem];
      this.detailList.set(newList);

      // Show success message for every item addition
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.messages.success'),
        detail: this.translateService.instant(
          'inbound.messages.item_detail_added_successfully',
        ),
      });
    }
    this.updateDetailTableData();
  }

  private initializeConfigurations(): void {
    // Store translated section titles for consistent comparison
    this.itemDetailsSectionTitle = this.translateService.instant(
      'inbound.sections.item_details',
    );
    this.logisticsSectionTitle = this.translateService.instant(
      'inbound.sections.logistics_information',
    );
  }

  private updateHeaderActions(): void {
    const translatedActions = INBOUND_HEADER_ACTIONS.map((action) => ({
      ...action,
      label: this.translateService.instant(action.label),
    }));
    this.headerActions.set(translatedActions);
  }

  private loadOptions(): void {
    // The form configuration already has the correct options defined
    // We just need to ensure they are properly translated
    this.updateFormFields();
  }

  private showWarningIfDetailListEmpty(): void {
    if (this.detailList().length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'inbound.messages.at_least_one_item_detail_required_warning',
        ),
      });
    }
  }

  // Helper method for displaying inbound type labels
  private getInboundTypeLabel(inputType: string): string {
    const typeMap: { [key: string]: string } = {
      PO: this.translateService.instant('inbound.type.purchase_order'),
      TO: this.translateService.instant('inbound.type.transfer_order'),
      RO: this.translateService.instant('inbound.type.return_order'),
    };
    return typeMap[inputType] || inputType; // Fallback to raw value if not found
  }

  // Get the form from AddFormComponent
  getForm(): any {
    return this.addFormComponent?.form;
  }

  private loadPurchaseOrderOptions(): void {
    // Load purchase orders with status VENDOR_CONFIRMED only (API limit: max 100)
    this.purchaseOrderService
      .getPurchaseOrders({
        page: 0,
        size: 100,
        sortBy: 'creationDate',
        ascending: false,
        status: 'VENDOR_CONFIRMED',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const options = (response.data || []).map((po: any) => ({
            label: po.series || po.code || '',
            value: po.series || po.code || '',
          }));
          this.purchaseOrderOptions.set(options);
          this.updatePurchaseOrderFormFields();
        },
        error: (error: any) => {
          console.error('Failed to load purchase orders:', error);
        },
      });
  }

  private updatePurchaseOrderFormFields(): void {
    const options = this.purchaseOrderOptions();
    if (options.length === 0) return;

    const currentFields = this.formFields();
    const updatedFields = currentFields.map((field) => {
      if (field.controlName === 'frontRecordCode') {
        return {
          ...field,
          options: [...options],
        };
      }
      return field;
    });

    this.formFields.set(updatedFields);
  }

  private loadOutboundDeliveryOrderOptions(): void {
    // Load outbound delivery orders with status PARTIALLY_SHIPPED (4) and SHIPPED (3)
    // Filter by outType = 'transfer-out-stock' (điều chuyển kho)
    const apiParams: OutboundDeliveryOrderPageDTO = {
      page: 0,
      size: 100,
      sortBy: 'creationDate',
      ascending: false,
      outType: 'transfer-out-stock',
      status: [3, 4], // SHIPPED (3) and PARTIALLY_SHIPPED (4)
    };

    this.outboundDeliveryOrderService
      .page4(apiParams)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: any) => {
          // Handle blob response
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        catchError((error: any) => {
          console.error('Failed to load outbound delivery orders:', error);
          return of({ pageContent: [], totalElements: 0 });
        }),
      )
      .subscribe({
        next: (response: any) => {
          const pageContent = BlobConverterUtil.extractPageContent(
            response,
            [],
          );
          const options = (pageContent || []).map((odo: any) => ({
            label: odo.code || '',
            value: odo.code || '',
            id: odo.id, // Store ID for API calls
          }));
          this.outboundDeliveryOrderOptions.set(options);
          this.updateOutboundDeliveryOrderFormFields();
        },
        error: (error: any) => {
          console.error('Failed to load outbound delivery orders:', error);
        },
      });
  }

  private updateOutboundDeliveryOrderFormFields(): void {
    const options = this.outboundDeliveryOrderOptions();
    if (options.length === 0) return;

    const currentFields = this.formFields();
    const updatedFields = currentFields.map((field) => {
      if (field.controlName === 'frontRecordCode') {
        return {
          ...field,
          options: [...options],
        };
      }
      return { ...field };
    });

    this.formFields.set(updatedFields);
  }

  private loadFrontRecordCodeOptions(inputType: string): void {
    if (inputType === InboundType.PURCHASE_ORDER) {
      // Load PO options for purchase order
      this.loadPurchaseOrderOptions();
    } else if (inputType === InboundType.TRANSFER_ORDER) {
      // Load outbound delivery order options for transfer order
      this.loadOutboundDeliveryOrderOptions();
    }
  }

  private handlePurchaseOrderChange(formData: any): any {
    const form = this.getForm();
    if (!form) return formData;

    const currentPOCode = formData.frontRecordCode;
    const previousPOCode = this.currentFormData?.frontRecordCode;

    // Check if PO selection changed (including when PO is cleared/removed)
    if (currentPOCode !== previousPOCode) {
      // Clear notice selection when PO changes or is removed
      const noticeControl = form.get('inboundNoticeId');
      if (noticeControl) {
        noticeControl.setValue(null, { emitEvent: false });
      }

      // Clear notice options
      this.inboundNoticeOptions.set([]);
      this.updateInboundNoticeFormFields();

      // Clear detail list when PO changes
      this.detailList.set([]);
      this.updateDetailTableData();

      // Only load notices if PO code is provided
      if (currentPOCode) {
        // Load notices for selected PO
        this.loadInboundNoticesBySourceCode(currentPOCode);
      }
    }

    return formData;
  }

  private handleOutboundDeliveryOrderChange(formData: any): any {
    const form = this.getForm();
    if (!form) return formData;

    const currentODOCode = formData.frontRecordCode;
    const previousODOCode = this.currentFormData?.frontRecordCode;

    // Check if ODO selection changed (including when ODO is cleared/removed)
    if (currentODOCode !== previousODOCode) {
      // Clear notice selection when ODO changes or is removed
      const noticeControl = form.get('inboundNoticeId');
      if (noticeControl) {
        noticeControl.setValue(null, { emitEvent: false });
      }

      // Clear notice options
      this.inboundNoticeOptions.set([]);
      this.updateInboundNoticeFormFields();

      // Clear detail list when ODO changes
      this.detailList.set([]);
      this.updateDetailTableData();

      // Only load inbound notices if ODO code is provided
      // Use ODO code as sourceCode to get inbound notices
      if (currentODOCode) {
        this.loadInboundNoticesBySourceCode(currentODOCode);
      }
    }

    return formData;
  }

  private loadInboundNoticesBySourceCode(
    sourceCode: string,
    callback?: () => void,
  ): void {
    // Call API /v1/inbound-notice/source-code/{sourceCode}
    const url = `${this.basePath}/v1/inbound-notice/source-code/${sourceCode}?status=PENDING`;
    this.http
      .get<any>(url, {
        headers: new HttpHeaders({ Accept: 'application/json' }),
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        catchError((error: any) => {
          console.error('Failed to load inbound notices:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.notice.messages.failed_to_load',
            ),
          });
          this.inboundNoticeOptions.set([]);
          this.updateInboundNoticeFormFields();
          return of([]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (notices: any[]) => {
          const options = (notices || []).map((notice: any) => ({
            label: notice.code || '',
            value: notice.id?.toString() || '',
            code: notice.code || '',
          }));
          this.inboundNoticeOptions.set(options);
          this.updateInboundNoticeFormFields();
        },
      });
  }

  private updateInboundNoticeFormFields(): void {
    const options = this.inboundNoticeOptions();
    const currentFields = this.formFields();
    const updatedFields = currentFields.map((field) => {
      if (field.controlName === 'inboundNoticeId') {
        return {
          ...field,
          options: [...options],
        };
      }
      return field;
    });

    this.formFields.set(updatedFields);
  }

  private handleInboundNoticeChange(formData: any): any {
    const form = this.getForm();
    if (!form) return formData;

    const currentNoticeId = formData.inboundNoticeId;
    const previousNoticeId = this.currentFormData?.inboundNoticeId;

    // Check if notice selection changed or was cleared
    if (currentNoticeId !== previousNoticeId) {
      if (currentNoticeId && currentNoticeId !== previousNoticeId) {
        // Load notice details and fill items
        this.loadInboundNoticeDetails(currentNoticeId);
      } else if (!currentNoticeId && previousNoticeId) {
        // Notice was cleared/removed - clear related data
        // Set flag to prevent warehouse mapping
        this.isClearingNotice = true;

        // Clear form controls immediately (formData already has empty values from onFormValueChanges)
        const warehouseCodeControl = form.get('receivingLogicWarehouseCode');
        if (warehouseCodeControl) {
          if (warehouseCodeControl.disabled) {
            warehouseCodeControl.enable({ emitEvent: false });
          }
          warehouseCodeControl.setValue('', { emitEvent: false });
        }

        const warehouseNameControl = form.get('receivingLogicWarehouseName');
        if (warehouseNameControl) {
          if (warehouseNameControl.disabled) {
            warehouseNameControl.enable({ emitEvent: false });
          }
          warehouseNameControl.setValue('', { emitEvent: false });
        }

        const warehouseIdControl = form.get('receivingLogicWarehouseId');
        if (warehouseIdControl) {
          if (warehouseIdControl.disabled) {
            warehouseIdControl.enable({ emitEvent: false });
          }
          warehouseIdControl.setValue(null, { emitEvent: false });
        }

        // Reset form fields to normal mode (not readonly) when notice is cleared
        // This will remove defaultValue from form fields config
        this.resetFormFieldsFromNoticeMode();

        // Clear detail list
        this.detailList.set([]);
        this.updateDetailTableData();

        // Use requestAnimationFrame to ensure DOM is updated, then clear again
        requestAnimationFrame(() => {
          setTimeout(() => {
            // Clear again to ensure values stay cleared even if form fields update triggered re-render
            const codeControl = form.get('receivingLogicWarehouseCode');
            if (codeControl) {
              codeControl.setValue('', { emitEvent: false });
            }
            const nameControl = form.get('receivingLogicWarehouseName');
            if (nameControl) {
              nameControl.setValue('', { emitEvent: false });
            }
            const idControl = form.get('receivingLogicWarehouseId');
            if (idControl) {
              idControl.setValue(null, { emitEvent: false });
            }

            // Reset flag after clearing is complete
            this.isClearingNotice = false;
          }, 200);
        });

        // Return cleared data (formData already has empty warehouse values)
        return formData;
      }
    }

    return formData;
  }

  private loadInboundNoticeDetails(noticeId: string): void {
    // Call API /v1/inbound-notice/{id}
    const url = `${this.basePath}/v1/inbound-notice/${noticeId}`;
    this.http
      .get<any>(url, {
        headers: new HttpHeaders({ Accept: 'application/json' }),
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        catchError((error: any) => {
          console.error('Failed to load inbound notice details:', error);
          const errorMessage =
            error?.error?.message || extractErrorMessage(error);
          const translatedMessage =
            this.errorTranslatorService.translate(errorMessage);
          this.errorDialogService.showError(translatedMessage);
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (notice: any) => {
          if (notice) {
            // Check noticeType to determine inputType
            // TRANSFER → inputType = TRANSFER_ORDER (điều chuyển kho)
            // PURCHASE_ORDER → inputType = PURCHASE_ORDER (đơn mua hàng)
            const noticeType = notice.noticeType;
            let targetInputType = InboundType.PURCHASE_ORDER; // Default

            if (noticeType === 'TRANSFER') {
              targetInputType = InboundType.TRANSFER_ORDER;
            } else if (noticeType === 'PURCHASE_ORDER') {
              targetInputType = InboundType.PURCHASE_ORDER;
            }

            // Set inputType based on noticeType (without emitEvent to avoid clearing data)
            const form = this.getForm();
            if (form) {
              const inputTypeControl = form.get('inputType');
              if (inputTypeControl) {
                inputTypeControl.setValue(targetInputType, {
                  emitEvent: false,
                });
              }
            }
            this.currentFormData = {
              ...(this.currentFormData || {}),
              inputType: targetInputType,
            };
            // Update form fields to reflect the change
            this.updateFormFields();

            // Use requestAnimationFrame to ensure form is ready
            requestAnimationFrame(() => {
              setTimeout(() => {
                // Fill warehouse from notice
                const form = this.getForm();
                if (!form) {
                  return;
                }

                const warehouseCode = notice.warehouseCode;
                const warehouseName = notice.warehouseName;

                const cachedWarehouse = warehouseCode
                  ? this.findWarehouseByCode(warehouseCode)
                  : null;
                const mappedWarehouseName =
                  cachedWarehouse?.name || warehouseName;
                let warehouseId = notice.warehouseId || cachedWarehouse?.id;

                // Set warehouse code
                if (warehouseCode) {
                  const warehouseCodeControl = form.get(
                    'receivingLogicWarehouseCode',
                  );
                  if (warehouseCodeControl) {
                    warehouseCodeControl.setValue(warehouseCode, {
                      emitEvent: false,
                    });
                  }
                }

                // Set warehouse name
                if (mappedWarehouseName) {
                  const warehouseNameControl = form.get(
                    'receivingLogicWarehouseName',
                  );
                  if (warehouseNameControl) {
                    warehouseNameControl.setValue(mappedWarehouseName, {
                      emitEvent: false,
                    });
                  }
                }

                // Set warehouse ID
                if (warehouseId) {
                  const warehouseIdControl = form.get(
                    'receivingLogicWarehouseId',
                  );
                  if (warehouseIdControl) {
                    warehouseIdControl.setValue(warehouseId, {
                      emitEvent: false,
                    });
                  }
                }

                // Update currentFormData with warehouse values
                this.currentFormData = {
                  ...(this.currentFormData || {}),
                  receivingLogicWarehouseCode: warehouseCode,
                  receivingLogicWarehouseName: mappedWarehouseName,
                  receivingLogicWarehouseId: warehouseId,
                };

                // Load items from notice details
                if (notice.details && notice.details.length > 0) {
                  const items: DetailItem[] = notice.details.map(
                    (detail: any) => ({
                      id: detail.id?.toString() || '',
                      skuCode: detail.skuCode || '',
                      skuName: detail.skuName || '',
                      spuCode: detail.spuCode || '',
                      spuName: detail.spuName || '',
                      uom: detail.uom || '',
                      quantity: detail.expectedQty || 0,
                      plannedQty: detail.expectedQty || 0,
                    }),
                  );
                  this.detailList.set(items);
                  this.updateDetailTableData();
                }
              }, 100);
            });
          }
        },
      });
  }

  private loadNoticeDataAndFillForm(noticeId: string): void {
    // Set loading state to show loading indicator
    this.loading.set(true);

    // Load notice details from API /v1/inbound-notice/{id}
    const url = `${this.basePath}/v1/inbound-notice/${noticeId}`;
    this.http
      .get<any>(url, {
        headers: new HttpHeaders({ Accept: 'application/json' }),
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        catchError((error: any) => {
          console.error('Failed to load notice data:', error);
          this.loading.set(false);
          const errorMessage =
            error?.error?.message || extractErrorMessage(error);
          const translatedMessage =
            this.errorTranslatorService.translate(errorMessage);
          this.errorDialogService.showError(translatedMessage);
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (notice: any) => {
          if (notice) {
            // Wait for form to be ready, then fill data
            // Use a retry mechanism to ensure form is initialized
            this.waitForFormAndFill(notice);
          } else {
            this.loading.set(false);
          }
        },
      });
  }

  private waitForFormAndFill(notice: any, retryCount: number = 0): void {
    const maxRetries = 20;
    const form = this.getForm();

    if (form) {
      // Form is ready, fill data immediately
      this.fillFormFromNotice(notice);
      this.loading.set(false);
    } else if (retryCount < maxRetries) {
      // Form not ready yet, retry after a short delay
      setTimeout(() => {
        this.waitForFormAndFill(notice, retryCount + 1);
      }, 150); // Slightly increase delay
    } else {
      console.error('Form not ready after maximum retries');
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: 'Form initialization timeout. Please refresh the page.',
      });
    }
  }

  private fillFormFromNotice(notice: any): void {
    const form = this.getForm();
    if (!form) {
      return;
    }

    // Check noticeType to determine inputType
    // TRANSFER → inputType = TRANSFER_ORDER (điều chuyển kho)
    // PURCHASE_ORDER → inputType = PURCHASE_ORDER (đơn mua hàng)
    const noticeType = notice.noticeType;
    let targetInputType = InboundType.PURCHASE_ORDER; // Default

    if (noticeType === 'TRANSFER') {
      targetInputType = InboundType.TRANSFER_ORDER;
    } else if (noticeType === 'PURCHASE_ORDER') {
      targetInputType = InboundType.PURCHASE_ORDER;
    }

    // Set inputType based on noticeType (without emitEvent to avoid clearing data)
    const inputTypeControl = form.get('inputType');
    if (inputTypeControl) {
      inputTypeControl.setValue(targetInputType, { emitEvent: false });
    }
    this.currentFormData = {
      ...(this.currentFormData || {}),
      inputType: targetInputType,
    };
    // Update form fields to reflect the change
    this.updateFormFields();

    // Load items from notice details
    if (notice.details && notice.details.length > 0) {
      const items: DetailItem[] = notice.details.map((detail: any) => ({
        id: detail.id?.toString() || '',
        skuCode: detail.skuCode || '',
        skuName: detail.skuName || '',
        spuCode: detail.spuCode || '',
        spuName: detail.spuName || '',
        plannedQty: detail.expectedQty || 0,
      }));
      this.detailList.set(items);
      this.updateDetailTableData();
    }

    // Get PO code (sourceCode) and notice code from notice
    const poCode = notice.sourceCode;
    const noticeCode = notice.code;

    // Map warehouse first to get the values ready
    const warehouseCode = notice.warehouseCode;
    const warehouseNameFromNotice = notice.warehouseName;
    const cachedWarehouse = warehouseCode
      ? this.findWarehouseByCode(warehouseCode)
      : null;
    const mappedWarehouseName =
      cachedWarehouse?.name ||
      warehouseNameFromNotice ||
      warehouseCode ||
      '';

    // Update form fields to show as text/readonly instead of dropdown
    this.updateFormFieldsForNoticeMode(
      noticeCode,
      poCode,
      warehouseCode,
      mappedWarehouseName,
    );

    // Use a small delay to ensure form fields are updated, then set values
    // Using requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      setTimeout(() => {
        const form = this.getForm();
        if (!form) {
          return;
        }

        // Fill PO as text (readonly)
        if (poCode) {
          const poControl = form.get('frontRecordCode');
          if (poControl) {
            poControl.setValue(poCode, { emitEvent: false });
            poControl.disable({ emitEvent: false });
            // Also update currentFormData
            this.currentFormData = {
              ...(this.currentFormData || {}),
              frontRecordCode: poCode,
            };
          }
        }

        // Fill notice as text (readonly)
        if (noticeCode) {
          const noticeControl = form.get('inboundNoticeId');
          if (noticeControl) {
            noticeControl.setValue(noticeCode, { emitEvent: false });
            noticeControl.disable({ emitEvent: false });
          }
        }

        const finalMappedWarehouseName = mappedWarehouseName;

        // Set warehouse code
        if (warehouseCode) {
          const warehouseCodeControl = form.get('receivingLogicWarehouseCode');
          if (warehouseCodeControl) {
            warehouseCodeControl.setValue(warehouseCode, { emitEvent: false });
            warehouseCodeControl.disable({ emitEvent: false });
            // Also update currentFormData
            this.currentFormData = {
              ...(this.currentFormData || {}),
              receivingLogicWarehouseCode: warehouseCode,
            };
          }
        }

        // Set warehouse name
        if (finalMappedWarehouseName) {
          const warehouseNameControl = form.get('receivingLogicWarehouseName');
          if (warehouseNameControl) {
            warehouseNameControl.setValue(finalMappedWarehouseName, {
              emitEvent: false,
            });
            warehouseNameControl.disable({ emitEvent: false });
            // Also update currentFormData
            this.currentFormData = {
              ...(this.currentFormData || {}),
              receivingLogicWarehouseName: finalMappedWarehouseName,
            };
          }
        }
        // Also set warehouse ID if available
        let warehouseId = notice.warehouseId;
        if (warehouseId) {
          const warehouseIdControl = form.get('receivingLogicWarehouseId');
          if (warehouseIdControl) {
            warehouseIdControl.setValue(warehouseId, { emitEvent: false });
          }
        } else if (warehouseCode) {
          const foundWarehouse = this.findWarehouseByCode(warehouseCode);
          if (foundWarehouse?.id) {
            warehouseId = foundWarehouse.id;
            const warehouseIdControl = form.get('receivingLogicWarehouseId');
            if (warehouseIdControl) {
              warehouseIdControl.setValue(warehouseId, { emitEvent: false });
            }
          }
        }

        // Update currentFormData with mapped warehouse name and ID
        this.currentFormData = {
          ...(this.currentFormData || {}),
          receivingLogicWarehouseCode: warehouseCode,
          receivingLogicWarehouseName: finalMappedWarehouseName,
          receivingLogicWarehouseId: warehouseId,
        };
      }, 50);
    });
  }

  private updateFormFieldsForNoticeMode(
    noticeCode?: string,
    poCode?: string,
    warehouseCode?: string,
    warehouseName?: string,
  ): void {
    // Update form fields config to show as text/readonly instead of dropdown
    const currentFields = this.formFields();
    const updatedFields = currentFields.map((field) => {
      if (field.controlName === 'inputType') {
        // Disable inputType field when creating from notice
        return {
          ...field,
          disabled: true,
        };
      }
      if (field.controlName === 'frontRecordCode') {
        // Change to text type and set default value
        return {
          ...field,
          type: 'text' as const,
          disabled: true,
          readonly: true,
          defaultValue: poCode || '',
        };
      }
      if (field.controlName === 'inboundNoticeId') {
        // Change to text type and set default value
        return {
          ...field,
          type: 'text' as const,
          disabled: true,
          readonly: true,
          defaultValue: noticeCode || '',
        };
      }
      if (field.controlName === 'receivingLogicWarehouseCode') {
        // Change warehouse code to text type (readonly) when creating from notice
        return {
          ...field,
          type: 'text' as const,
          disabled: true,
          readonly: true,
          defaultValue: warehouseCode || '',
        };
      }
      if (field.controlName === 'receivingLogicWarehouseName') {
        // Change warehouse name to text type (readonly) when creating from notice
        return {
          ...field,
          type: 'text' as const,
          disabled: true,
          readonly: true,
          defaultValue: warehouseName || '',
        };
      }
      return field;
    });

    this.formFields.set(updatedFields);

    // Also disable form controls directly
    const form = this.getForm();
    if (form) {
      // Disable inputType control when creating from notice
      const inputTypeControl = form.get('inputType');
      if (inputTypeControl && !inputTypeControl.disabled) {
        inputTypeControl.disable({ emitEvent: false });
      }

      const poControl = form.get('frontRecordCode');
      if (poControl) {
        if (poCode) {
          poControl.setValue(poCode, { emitEvent: false });
        }
        if (!poControl.disabled) {
          poControl.disable({ emitEvent: false });
        }
      }

      const noticeControl = form.get('inboundNoticeId');
      if (noticeControl) {
        if (noticeCode) {
          noticeControl.setValue(noticeCode, { emitEvent: false });
        }
        if (!noticeControl.disabled) {
          noticeControl.disable({ emitEvent: false });
        }
      }
    }
  }

  private resetFormFieldsFromNoticeMode(): void {
    // Reset form fields back to normal mode (dropdown/searchable) when notice is cleared
    const originalConfig = INBOUND_ADD_FORM_CONFIG;
    const currentFields = this.formFields();

    const updatedFields = currentFields.map((field) => {
      // Find original config for this field
      const originalField = originalConfig.find(
        (f) => f.controlName === field.controlName,
      );
      if (!originalField) {
        return field;
      }

      if (field.controlName === 'frontRecordCode') {
        // Reset to searchableDropdown, remove defaultValue
        const resetField = {
          ...originalField,
          options: field.options || originalField.options || [],
        };
        delete resetField.defaultValue; // Remove defaultValue to prevent it from being used
        return resetField;
      }
      if (field.controlName === 'inboundNoticeId') {
        // Reset to searchableDropdown, remove defaultValue
        const resetField = {
          ...originalField,
          options: field.options || originalField.options || [],
        };
        delete resetField.defaultValue; // Remove defaultValue to prevent it from being used
        return resetField;
      }
      if (field.controlName === 'receivingLogicWarehouseCode') {
        // Reset to text (readonly) as per original config, remove defaultValue
        const resetField = {
          ...originalField,
        };
        delete resetField.defaultValue; // Remove defaultValue to prevent it from being used
        return resetField;
      }
      if (field.controlName === 'receivingLogicWarehouseName') {
        // Reset to text (readonly) as per original config, remove defaultValue
        const resetField = {
          ...originalField,
        };
        delete resetField.defaultValue; // Remove defaultValue to prevent it from being used
        return resetField;
      }
      if (field.controlName === 'inputType') {
        // Reset inputType field - remove disabled state
        const resetField = {
          ...originalField,
          options: field.options || originalField.options || [],
        };
        delete resetField.disabled; // Remove disabled to enable the field
        return resetField;
      }
      return field;
    });

    this.formFields.set(updatedFields);

    // Also clear form controls directly after resetting fields
    const form = this.getForm();
    if (form) {
      // Enable inputType control when resetting from notice mode
      const inputTypeControl = form.get('inputType');
      if (inputTypeControl && inputTypeControl.disabled) {
        inputTypeControl.enable({ emitEvent: false });
      }

      setTimeout(() => {
        const warehouseCodeControl = form.get('receivingLogicWarehouseCode');
        if (warehouseCodeControl) {
          warehouseCodeControl.setValue('', { emitEvent: false });
        }

        const warehouseNameControl = form.get('receivingLogicWarehouseName');
        if (warehouseNameControl) {
          warehouseNameControl.setValue('', { emitEvent: false });
        }

        const warehouseIdControl = form.get('receivingLogicWarehouseId');
        if (warehouseIdControl) {
          warehouseIdControl.setValue(null, { emitEvent: false });
        }
      }, 100);
    }
  }

  onBack(): void {
    // Navigate back to the main inbound page
    // Check for tab query parameter first, then default to inbound-warrant
    const tab = this.route.snapshot.queryParams['tab'] || 'inbound-warrant';

    this.router.navigate(['/stock/inbound'], {
      queryParams: { tab },
    });
  }

  onCellValueChange(event: { rowData: any; prop: string; value: any }): void {
    // Emit the change to the debounced subject instead of updating immediately
    // This prevents the input field from losing focus on every keystroke
    this.cellValueChange$.next(event);
  }

  private updateDetailListFromCellChange(event: {
    rowData: any;
    prop: string;
    value: any;
  }): void {
    // Update the detailList signal with the new value (debounced)
    const updatedDetailList = this.detailList().map((item) =>
      item.id === event.rowData.id
        ? { ...item, [event.prop]: event.value }
        : item,
    );

    this.detailList.set(updatedDetailList);
  }
}
